'use client';

import React, { useRef } from 'react';

import { IconCalendar } from '@icons';
import Input from './Input';

interface InputDateProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  className?: string;
  hint?: React.ReactNode;
  hintClassName?: string;
  hintId?: string;
  hintHidden?: boolean;
}

const InputDate = React.forwardRef<HTMLInputElement, InputDateProps>(
  (
    { className = '', label, hint, hintClassName, hintId, hintHidden, id, disabled, ...props },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleIconClick = () => {
      const node = inputRef.current;
      if (!node || disabled) {
        return;
      }
      if (typeof node.showPicker === 'function') {
        try {
          node.showPicker();
          return;
        } catch {
          // Fall through to focus for browsers that block showPicker.
        }
      }
      try {
        node.click();
      } catch {
        // Ignore click errors; focus is the fallback.
      }
      node.focus();
    };

    return (
      <>
        {label ? <label htmlFor={id}>{label}</label> : null}
        <div className='input-affix input-date'>
          <Input
            id={id}
            ref={setRefs}
            className={className}
            type='date'
            hint={undefined}
            disabled={disabled}
            {...props}
          />
          <button
            type='button'
            className='affix-icon affix-icon-button'
            aria-label='Open calendar'
            onClick={handleIconClick}
            disabled={disabled}
          >
            <IconCalendar />
          </button>
        </div>
        {hint ? (
          <small
            id={hintId}
            hidden={hintHidden}
            className={`hint${hintClassName ? ' ' + hintClassName : ''}`}
          >
            {hint}
          </small>
        ) : null}
      </>
    );
  }
);

InputDate.displayName = 'InputDate';

export default InputDate;
